/* eslint-disable @next/next/no-img-element */
import { Know, KnowTranscript } from 'interfaces/Know'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import { useEffect, useRef, useState } from 'react'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { getAltTextFromTranscript } from 'lib/features/Know/know'
import Stack from 'components/elements/Stack'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import { getReferrerScreen } from 'lib/formatting/url'
import { useRouter } from 'next/router'
import PNGSignupBanner from '../PNGSignupBanner'
import KnowSEOBelowFoldSection from '../KnowSEOBelowFoldSection'
import { Flashcard } from 'interfaces/Flashcard'
import NextImage from 'components/elements/NextImage'
import AiEnhancedPNGHeroSection from './AiEnhancedPNGHeroSection'
import AIEnhancedPNGSidebar from './AIEnhancedPNGSidebar'
import { Body2, Headline3 } from 'stories/elements/Typography/Text'
import AiEnhancedArrow from 'assets/know/ai-enhanced-arrow.svg'
import PractisingElementModal from '../PractisingElementModal'
import _useTranslation from 'lib/hooks/_useTranslation'
import KnowService from 'services/KnowService'
import ImageSignupBanner from '../ImageSignupBanner'
import { ComponentContainer } from 'components/layouts/Layouts.css'
import RelatedKnowCard from 'stories/elements/Know/RelatedKnowCard'
import {
    AnalyticsEventKnowConversionImageButtonClick,
    AnalyticsEventKnowImagePageImpression,
    AnalyticsEventSimilarKnowsButtonClick,
} from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'
import ReactMarkdown from 'react-markdown'
import { ArticleProgress, ContentWrapper } from 'components/feature/Article/Article.styles'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import ViewIcon from 'assets/icons/webapp_views.svg'
import WebappKnowCard from '../../../webapp/WebappKnowCard'
import { ActiveExperiments } from 'lib/features/activeExperiments'
import { useVisiblePage } from 'lib/hooks/useVisiblePage'

const NATIVE_PDF_WIDTH = 400

const Container = styled.div<{ isDarkTheme?: boolean }>`
    position: relative;
    display: grid;
    justify-content: center;
    padding: 0px;
    min-height: 100vh;
    width: 100%;

    transition: all 0.35s;
    .hidden {
        display: none;
    }

    @media (max-width: 1350px) {
        .stack,
        .hero {
            position: relative;
            right: 75px;
        }
    }
    @media (max-width: 1150px) {
        .stack,
        .hero {
            right: unset;
        }
        .sidebar {
            visibility: hidden;
        }
    }
    @media (max-width: 1050px) {
        .sidebar {
            visibility: visible;
        }
    }

    ${({ isDarkTheme }) =>
        isDarkTheme
            ? css`
                  background-color: ${theme.colors.darkThemeBackground};
              `
            : ''};

    @media (max-width: 750px) {
        .hidden-mobile-know-content-wrapper {
            display: none;
        }
    }
`

const QuizContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`

const ImagesWrapper = styled.div<{
    isDarkTheme?: boolean
    hasFlashcards?: boolean
    isMobile?: boolean
}>`
    display: grid;
    place-content: start center;
    width: 100%;
    height: fit-content;
    position: relative;
    gap: 15px;
    padding: 10px 10%;
    overflow: visible;

    .stack,
    .box {
        position: relative;
        border-radius: ${theme.borderRadius.normal};
    }

    .box {
        border-radius: ${theme.borderRadius.normal};
        overflow: hidden;
    }

    .box {
        @media (max-width: 1050px) {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
        }
    }

    .quiz {
        position: relative;
        top: -100px;
        display: hidden;
    }

    .button {
        svg path {
            fill: ${theme.colors.white};
        }
        background: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.darkThemePrimary : theme.colors.deepSeaBlue)};
        border: 2px solid #e5e7eb;
        width: fit-content;
        height: 50px;
        position: absolute;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 14px 24px;
        border-radius: ${theme.borderRadius.normal};
    }

    min-height: 400px;
    background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.darkThemeSecondary : theme.colors.white)};
    .quiz {
        min-height: ${(props) => (props.hasFlashcards ? '200px' : '0')};
        transition: min-height 0.3s ease;
    }

    @media (max-width: 1050px) {
        overflow: hidden;
        padding: 0px 5%;
    }
`

const Content = styled.div<{ isDarkTheme?: boolean }>`
    display: grid;
    gap: 16px;
    overflow: hidden;
    background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.darkThemeSecondary : theme.colors.white)};
    .background {
        padding-top: 75px;
        width: 100vw;
        margin: 0;
        background-color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.darkThemeSecondary : theme.colors.white)};
        display: flex;
        justify-content: center;

        background: ${({ isDarkTheme }) =>
            isDarkTheme
                ? css`linear-gradient(180deg, #172554 0%, #5a6c9d 100%)`
                : css`
                        linear-gradient(180deg, #dbeafe 0%, rgba(255, 255, 255, 0) 100%)
                        `};

        ${({ isDarkTheme }) =>
            isDarkTheme
                ? css`
                      background-color: ${theme.colors.darkThemeSecondary};
                  `
                : ''};

        @media (max-width: 750px) {
            padding-top: 75px;
            width: 100%;
        }
    }
`

const InfoBracket = styled.div<{ isDarkTheme?: boolean; revertOrder?: boolean }>`
    border: 2px dashed ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.darkThemePrimary : theme.colors.blueIce)};
    padding: 25px;
    display: grid;
    gap: 15px;
    border-radius: ${theme.borderRadius.normal};
    max-width: 1024px;
    position: relative;
    transform: rotate(-0.5deg);
    svg {
        position: absolute;
        z-index: ${theme.zIndex.FAB};
        ${({ revertOrder }) =>
            revertOrder
                ? css`
                      top: -75px;
                  `
                : css`
                      bottom: -75px;
                  `}

        left: -65px;
        ${({ isDarkTheme }) =>
            isDarkTheme
                ? css`
                      path {
                          fill: ${theme.colors.darkThemePrimary};
                      }
                  `
                : ''};
    }
    @media (max-width: 1050px) {
        width: 100%;
        transform: rotate(0deg);
    }
`

const KnowGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 50px;
    @media (max-width: 1150px) {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }
`

const Wrapper = styled.div<{
    isSidebarShown?: boolean
    blurImage: boolean
    blurHalfOfFirstImage: boolean
    isDarkTheme?: boolean
}>`
    position: relative;
    ${({ isDarkTheme }) =>
        !isDarkTheme
            ? css`
                  border: 2px solid #e7f0f6;
                  padding: 5px;
                  background: #f5fafc;
              `
            : ''}
    border-radius: ${theme.borderRadius.normal};
    overflow-x: visible;

    img {
        box-shadow: rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px;
        border-radius: ${theme.borderRadius.normal};
        max-width: 100%;
        max-height: 100%;
        height: auto !important;
        max-width: 1024px;
        user-select: none;
        -webkit-touch-callout: none;
        pointer-events: none;
    }

    ${({ blurImage }) =>
        blurImage
            ? css`
                  filter: blur(3px);
                  -webkit-filter: blur(3px);
              `
            : ''}

    ${({ blurHalfOfFirstImage }) =>
        blurHalfOfFirstImage
            ? css`
                  position: relative;

                  &::after {
                      content: '';
                      position: absolute;
                      bottom: 0;
                      left: 0;
                      width: 100%;
                      height: 50%;
                      backdrop-filter: blur(3px);
                      -webkit-backdrop-filter: blur(3px);
                      pointer-events: none;
                  }
              `
            : ''}
`

const ArrowIcon = styled(AiEnhancedArrow)<{ revert?: boolean }>`
    ${({ revert }) =>
        revert
            ? css`
                  transform: scaleX(-1) rotate(180deg);
              `
            : ''}
`

interface Props {
    knowSeoInfo: KnowSeoInfo
    similarKnows: Know[] | null
    transcript?: KnowTranscript | null
    flashcards?: Flashcard[] | null
    unblurContentForSmallerMarkets: boolean
    isMobile?: boolean
    experiments: ActiveExperiments
}

const AIEnhancedPNGPreview = ({
    knowSeoInfo,
    similarKnows,
    transcript,
    flashcards,
    unblurContentForSmallerMarkets,
    isMobile,
    experiments,
}: Props) => {
    const [generatedFlashcards, setGeneratedFlashcards] = useState<Flashcard[]>()
    const imageContainerRef = useRef<HTMLDivElement | null>(null)
    const heroContainerRef = useRef<HTMLDivElement>(null)
    const similarKnowsContainerRef = useRef<HTMLDivElement>(null)
    const quizContainerRef = useRef<HTMLDivElement>(null)
    const router = useRouter()
    const { t } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('common')
    const referrerScreen = getReferrerScreen(router)
    const minXL = !useMedia(deviceSize.minXL)
    const isDarkTheme = true
    const blurInitialMobilePDFPage = !!isMobile

    const [showSidebar, setShowSidebar] = useState(true)
    const knowContentRef = useRef<HTMLDivElement | null>(null)

    const { know, seoPages } = knowSeoInfo
    const downloadDeepLinkPath = appDeepLinkKnow(know.uuid)
    const [pdfWidth, setPDFWidth] = useState<number>(
        imageContainerRef.current?.offsetWidth ? imageContainerRef.current.offsetWidth * 0.9 : NATIVE_PDF_WIDTH
    )

    useEffect(() => {
        const resizeHandler = () => {
            if (imageContainerRef.current) {
                const width = imageContainerRef.current.offsetWidth * 0.9
                setPDFWidth(width)
            }
        }

        window.addEventListener('resize', resizeHandler)
        resizeHandler()

        return () => {
            window.removeEventListener('resize', resizeHandler)
        }
    }, [])

    useEffect(() => {
        if (!know) return

        let mounted = true

        KnowService.getGeneratedFlashcards(know.uuid)
            .then((data) => {
                if (mounted && data) {
                    setGeneratedFlashcards(data)
                }
            })
            .catch((error) => {
                if (mounted) {
                    console.error('Unable to fetch generated flashcards', error)
                }
            })

        return () => {
            mounted = false
        }
    }, [know])

    const onSimilarKnowsClicked = (know: Know) => {
        trackEvent(AnalyticsEventSimilarKnowsButtonClick, { know, referrerScreen })
    }

    const onContentClick = () => {
        if (!isMobile) return
        trackEvent(AnalyticsEventKnowConversionImageButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen,
        })

        redirectToAppDownloadUrlAsync('dynamic', 'know-mobile-ai-enhanced-hero', downloadDeepLinkPath)
    }

    const Card = isMobile ? WebappKnowCard : RelatedKnowCard

    const knowPages = !unblurContentForSmallerMarkets
        ? know.knowDocumentPages.slice(isMobile ? 1 : 0, 10)
        : know.knowDocumentPages

    const { visiblePage, observe, cleanup } = useVisiblePage()

    useEffect(() => {
        const wrappers = document.querySelectorAll('.stack')
        observe(Array.from(wrappers))

        return () => {
            if (cleanup) cleanup()
        }
    }, [observe, knowPages, cleanup])

    useEffect(() => {
        trackEvent(AnalyticsEventKnowImagePageImpression, {
            valueId: visiblePage ?? 0,
            value: visiblePage?.toString(),
            know,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visiblePage])

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.target.id === 'know-content') {
                        // Only hide if we've scrolled past 600px AND the element is not intersecting
                        const scrollPosition = window.scrollY
                        setShowSidebar(scrollPosition <= 600 || (scrollPosition >= 600 && entry.isIntersecting))
                    }
                })
            },
            { threshold: 0 }
        )

        const currentRef = knowContentRef.current
        if (currentRef) {
            observer.observe(currentRef)
        }
    }, [])

    return (
        <Container id="hero" isDarkTheme={isDarkTheme}>
            {isMobile ? <ArticleProgress fromBottom={0} /> : null}
            {!isMobile ? (
                <div className="sidebar" style={{ display: isMobile || showSidebar ? 'block' : 'none' }}>
                    <AIEnhancedPNGSidebar
                        know={know}
                        currentActivePage={visiblePage}
                        settings={{
                            hasFlashcards: !!generatedFlashcards?.length,
                            hasSimilarKnows: !!similarKnows?.length,
                            isMobile: isMobile,
                            isDarkTheme,
                        }}
                    />
                </div>
            ) : null}
            <Content isDarkTheme={isDarkTheme}>
                <div className="background" ref={heroContainerRef}>
                    <AiEnhancedPNGHeroSection
                        knowSeoInfo={knowSeoInfo}
                        referrerScreen={referrerScreen}
                        isMobile={isMobile}
                        experiments={experiments}
                        onContentClick={onContentClick}
                        isDarkTheme={isDarkTheme}
                    />
                </div>

                <ImagesWrapper
                    id="know-content"
                    ref={(el) => {
                        if (el) {
                            imageContainerRef.current = el
                            knowContentRef.current = el
                        }
                    }}
                    isDarkTheme={isDarkTheme}
                    hasFlashcards={!!generatedFlashcards?.length}
                    isMobile={isMobile}
                    className={isMobile && !knowPages.length ? 'hidden-mobile-know-content-wrapper' : ''}
                >
                    {knowPages?.map((page, i) => (
                        <Stack key={`wrapper-${page.uuid}`} className="stack" gutter={25} data-page-index={i}>
                            <div className="box" onClick={onContentClick} id={`page-${i.toString()}`}>
                                <Wrapper
                                    blurImage={unblurContentForSmallerMarkets ? false : i > 0 || blurInitialMobilePDFPage}
                                    blurHalfOfFirstImage={
                                        unblurContentForSmallerMarkets ? false : !(knowPages.length > 1) || !minXL
                                    }
                                    isDarkTheme={isDarkTheme}
                                    key={page.uuid}
                                >
                                    <Stack gutter={25} onContextMenu={(e) => e.preventDefault()}>
                                        <NextImage
                                            priority={i === 0}
                                            width={isMobile ? pdfWidth : 1024}
                                            height={isMobile ? Math.round(pdfWidth * (380 / 1024)) : 1440}
                                            src={page.imageUrl}
                                            alt={getAltTextFromTranscript(transcript ?? null) ?? `know - ${page.pageNumber}`}
                                            loading={i > 0 ? 'lazy' : 'eager'}
                                        />
                                    </Stack>
                                </Wrapper>
                                {isMobile ? (
                                    <>
                                        {i > 0 || blurInitialMobilePDFPage ? (
                                            <div className="button">
                                                <ViewIcon />

                                                <Body2
                                                    fontSize={15}
                                                    color={theme.colors.white}
                                                    fontFamily="var(--font-inter)"
                                                    fontWeight={500}
                                                >
                                                    {tCommon('common/view')}
                                                </Body2>
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    <>
                                        {!unblurContentForSmallerMarkets ? (
                                            <ImageSignupBanner
                                                knowSeoInfo={knowSeoInfo}
                                                index={i}
                                                isSidebarShown={false}
                                                isDarkTheme={isDarkTheme}
                                                experiments={experiments}
                                            />
                                        ) : null}
                                    </>
                                )}
                            </div>
                            {!isMobile ? (
                                <>
                                    {unblurContentForSmallerMarkets ? (
                                        <div className="fullwidth">
                                            <PNGSignupBanner
                                                knowSeoInfo={knowSeoInfo}
                                                isSidebarShown={true}
                                                disableWrapperStyling
                                                index={i}
                                                isDarkTheme={isDarkTheme}
                                            />
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                            {seoPages?.length && seoPages[i] ? (
                                <InfoBracket isDarkTheme={isDarkTheme} revertOrder>
                                    <ArrowIcon revert />

                                    <ContentWrapper isDarkTheme={isDarkTheme}>
                                        <ReactMarkdown>{seoPages[i].summary}</ReactMarkdown>
                                    </ContentWrapper>
                                </InfoBracket>
                            ) : null}
                        </Stack>
                    ))}
                </ImagesWrapper>
                <div className="quiz" id="quiz" ref={quizContainerRef} />
                {generatedFlashcards?.length ? (
                    <QuizContainer>
                        <PractisingElementModal
                            know={know}
                            flashcards={generatedFlashcards}
                            isDarkTheme={isDarkTheme}
                            isMobile={isMobile}
                        />
                    </QuizContainer>
                ) : null}
                <div id="similar-knows" />

                <div className="below-fold">
                    {similarKnows?.length ? (
                        <ComponentContainer
                            title="Similar know"
                            ref={similarKnowsContainerRef}
                            color={isDarkTheme ? theme.colors.darkThemeSecondary : undefined}
                        >
                            <section>
                                <Stack gutter={40}>
                                    <Headline3
                                        fontSize={20}
                                        fontFamily="var(--font-inter)"
                                        fontWeight={600}
                                        color={isDarkTheme ? theme.colors.white : theme.colors.subtitleBlack}
                                        textAlign="center"
                                    >
                                        {t('knows/similarContentHeading')}
                                    </Headline3>

                                    <KnowGrid>
                                        {similarKnows.map((know) => (
                                            <span key={know.uuid} onClick={() => onSimilarKnowsClicked(know)}>
                                                <Card
                                                    know={know}
                                                    referrerScreen={'ai-enhanced-similar-knows'}
                                                    knowUuid={know.uuid}
                                                    isDarkTheme={isDarkTheme}
                                                />
                                            </span>
                                        ))}
                                    </KnowGrid>
                                </Stack>
                            </section>
                        </ComponentContainer>
                    ) : null}
                    <KnowSEOBelowFoldSection
                        transcript={transcript}
                        knowSeoInfo={knowSeoInfo}
                        flashcards={flashcards}
                        isDarkTheme={isDarkTheme}
                    />
                </div>
            </Content>
        </Container>
    )
}

export default AIEnhancedPNGPreview
